import { useTranslation } from 'react-i18next';
import styles from './gift-card-detail.module.scss';
import Image from '@components/elements/image/image';
import Button from '@components/elements/button/button';
import InputEnvelope from '@components/elements/input-envelope/input-envelope';
import Input, { fieldToInputProps } from '@components/elements/input-2/input-2';
import InputArea from '@components/elements/input-area/input-area';
import cn from 'classnames';
import ArrowIcon from '@components/icons/arrow-icon';
import { formatCurrency } from '@utils/format';
import { useRouter } from 'next/router';
import { ActiveStep } from '../gift-card-layout/gift-card-layout';
import { isSome, Maybe } from '@crocodille/util';
import {
  PaymentMethodCode,
  v2_useInstantOrderCheckout,
} from '@crocodille/common';
import { v2_useOrderCompleteForm } from '@crocodille/common/lib/forms/order-complete-form.v2';
import { translateError } from '@utils/error';
import PaymentSelect, {
  fieldToPaymentSelectProps,
} from '@components/order/payment-select/payment-select';
import Switch, { fieldToSwitchProps } from '@components/elements/switch/switch';
import PhoneInput, {
  fieldToPhoneInputProps,
  filedToPhoneInputErrorTranslationParams,
} from '@components/elements/phone-input/phone-input';
import React, { useMemo, useState } from 'react';
import paymentIconApple from '../../../../public/ico-paymentmethod-apple_pay.png';
import paymentIconAtBranch from '../../../../public/ico-paymentmethod-at_branch.png';
import paymentIconGoogle from '../../../../public/ico-paymentmethod-google_pay.png';
import paymentIconTwisto from '../../../../public/ico-paymentmethod-twisto.png';
import paymentIconZensys from '../../../../public/ico-paymentmethod-zensys.png';
import { isApplePaymentSupported } from '@components/payment/apple-payment';
import { isGooglePaymentSupported } from '@components/payment/google-payment';
import { usePaymentSupport } from '@hooks/use-payment-support';
import SpinnerIcon from '@components/icons/spinner-icon';
import Error from '@components/elements/error/error';

interface Product {
  readonly itemCode: string;
  readonly unitPrice: number | null;
}

interface CardFormProps {
  readonly isGift: boolean;
  readonly fromWho: string;
  readonly onFromWhoChange: (value: string) => void;
  readonly toWho: string;
  readonly onToWhoChange: (value: string) => void;
  readonly message: string;
  readonly onMessageChange: (value: string) => void;
  readonly email: string;
  readonly onEmailChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly orderTotal?: Maybe<number>;
}

const CardForm = (props: CardFormProps) => {
  const { t } = useTranslation();

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        props.onSubmit();
      }}
      className={styles.form}
    >
      <div>
        <InputEnvelope
          label={t('gift_card_configurator.from_whom')}
          noLabelElement
        >
          <Input
            type="text"
            name="fromWho"
            minLength={2}
            maxLength={30}
            placeholder={t('gift_card_configurator.from_whom')}
            readOnly={!props.isGift}
            value={props.fromWho}
            onChange={props.onFromWhoChange}
          />
        </InputEnvelope>
        <InputEnvelope
          label={t('gift_card_configurator.for_whom')}
          noLabelElement
        >
          <Input
            type="text"
            name="toWho"
            minLength={2}
            maxLength={30}
            placeholder={t('gift_card_configurator.for_whom')}
            readOnly={!props.isGift}
            value={props.toWho}
            onChange={props.onToWhoChange}
          />
        </InputEnvelope>
        <InputEnvelope
          label={t('gift_card_configurator.email')}
          noLabelElement
          required
        >
          <Input
            type="email"
            name="email"
            maxLength={70}
            placeholder={`*${t('gift_card_configurator.email')}`}
            value={props.email}
            onChange={props.onEmailChange}
            required
          />
        </InputEnvelope>
        <InputEnvelope
          label={t('gift_card_configurator.message')}
          noLabelElement
        >
          <InputArea
            placeholder={t('gift_card_configurator.message')}
            name="message"
            maxLength={500}
            readOnly={!props.isGift}
            value={props.message}
            onChange={props.onMessageChange}
          />
        </InputEnvelope>
      </div>
      <Button type="submit" className={cn(styles.button, styles.submit)}>
        {t('gift_card_configurator.check')}
        <Button.Icon placement="right">
          <ArrowIcon className={cn(styles.arrowIcon)} />
        </Button.Icon>
      </Button>
    </form>
  );
};

interface CardCheckoutFormProps {
  readonly checkoutForm: ReturnType<typeof v2_useOrderCompleteForm>;
  readonly paymentMethods: ReturnType<
    typeof v2_useInstantOrderCheckout
  >['paymentMethods'];
  readonly orderTotal: number;
  readonly error: Maybe<string>;
  readonly isBusy: boolean;
}

const CardCheckoutForm = (props: CardCheckoutFormProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { fields, handleSubmit } = props.checkoutForm;

  const { canMakeGooglePayPayments } = usePaymentSupport();

  const paymentMethods = useMemo(() => {
    return (
      props.paymentMethods
        //
        // Remove unsupported payment methods.
        //
        .filter((method) => {
          switch (method.code) {
            case PaymentMethodCode.APPLE_PAY: {
              return isApplePaymentSupported();
            }
            case PaymentMethodCode.GOOGLE_PAY: {
              return isGooglePaymentSupported() && canMakeGooglePayPayments;
            }
            default: {
              return true;
            }
          }
        })
        //
        // Add icons
        //
        .map((method) => {
          const value = method.value;
          const label = method.label;
          const description = method.description;

          let icon = <></>;

          switch (method.code) {
            case 'card': {
              icon = (
                <React.Fragment key={method.code}>
                  <Image src={paymentIconZensys} alt="Pay by card logo" />
                </React.Fragment>
              );
              break;
            }
            case PaymentMethodCode.APPLE_PAY: {
              icon = (
                <React.Fragment key={method.code}>
                  <Image
                    key={PaymentMethodCode.APPLE_PAY}
                    src={paymentIconApple}
                    alt="ApplePay logo"
                  />
                </React.Fragment>
              );
              break;
            }
            case PaymentMethodCode.GOOGLE_PAY: {
              icon = (
                <React.Fragment key={method.code}>
                  <Image
                    key={PaymentMethodCode.GOOGLE_PAY}
                    src={paymentIconGoogle}
                    alt="Google Pay logo"
                  />
                </React.Fragment>
              );
              break;
            }
            case PaymentMethodCode.TWISTO: {
              icon = (
                <React.Fragment key={method.code}>
                  <Image
                    key={PaymentMethodCode.TWISTO}
                    src={paymentIconTwisto}
                    alt="Twisto logo"
                  />
                </React.Fragment>
              );
              break;
            }
            default: {
              icon = (
                <React.Fragment key={method.code}>
                  <Image
                    key={PaymentMethodCode.CASH_COURIER}
                    src={paymentIconAtBranch}
                    alt="Cash"
                  />
                </React.Fragment>
              );
              break;
            }
          }

          return {
            value,
            label,
            description,
            icon,
            code: method.code,
            maxOrderValue: method.maxOrderValue,
          };
        })
    );
  }, [props.paymentMethods, canMakeGooglePayPayments]);

  return (
    <div className={styles.checkoutProcess}>
      <div className={styles.title}>{t('gift_card_configurator.check')}</div>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          handleSubmit();
        }}
        className={cn(styles.form, styles.checkoutProcessForm)}
      >
        <h2 className={styles.sectionHeading}>
          {t('gift_card_configurator.checkout_contact')}
        </h2>
        <div>
          <InputEnvelope
            label={t('checkout_process.email')}
            required={fields.email.required}
            error={translateError(t, fields.email.error)}
            noLabelElement
          >
            <Input
              type="email"
              autoComplete="email"
              {...fieldToInputProps(fields.email)}
              placeholder={t('checkout_process.email')}
            />
          </InputEnvelope>
          <PhoneInput
            label={t('checkout_process.phone')}
            error={translateError(t, fields.shippingAddressPhoneNumber.error, {
              ...filedToPhoneInputErrorTranslationParams(
                fields.shippingAddressPhoneNumber,
              ),
            })}
            {...fieldToPhoneInputProps(fields.shippingAddressPhoneNumber)}
          />
          <InputEnvelope
            className={styles.firstName}
            label={t('checkout_process.first_name')}
            required={fields.shippingAddressFirstName.required}
            error={translateError(t, fields.shippingAddressFirstName.error, {
              minLength: 2,
              maxLength: 30,
            })}
            noLabelElement
          >
            <Input
              type="text"
              autoComplete="given-name"
              minLength={2}
              maxLength={30}
              {...fieldToInputProps(fields.shippingAddressFirstName)}
              placeholder={t('checkout_process.first_name')}
            />
          </InputEnvelope>
          <InputEnvelope
            className={styles.lastName}
            label={t('checkout_process.last_name')}
            required={fields.shippingAddressLastName.required}
            error={translateError(t, fields.shippingAddressLastName.error, {
              minLength: 2,
              maxLength: 30,
            })}
            noLabelElement
          >
            <Input
              type="text"
              autoComplete="family-name"
              minLength={2}
              maxLength={30}
              {...fieldToInputProps(fields.shippingAddressLastName)}
              placeholder={t('checkout_process.last_name')}
            />
          </InputEnvelope>
          <InputEnvelope
            label={t('checkout_process.use_different_billing_address')}
            required={fields.useDifferentBillingAddress.required}
            error={translateError(t, fields.useDifferentBillingAddress.error)}
            inline
            isReverse
          >
            <Switch
              {...fieldToSwitchProps(fields.useDifferentBillingAddress)}
            />
          </InputEnvelope>
        </div>
        {fields.useDifferentBillingAddress.value ? (
          <div className={styles.billingAddress}>
            <h2 className={styles.sectionHeading}>
              {t('checkout_process.billing_address')}
            </h2>
            <InputEnvelope
              label={t('checkout_process.first_name')}
              required={fields.billingAddressFirstName.required}
              error={translateError(t, fields.billingAddressFirstName.error, {
                minLength: 2,
                maxLength: 30,
              })}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="given-name"
                minLength={2}
                maxLength={30}
                placeholder={t('checkout_process.first_name')}
                {...fieldToInputProps(fields.billingAddressFirstName)}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.last_name')}
              required={fields.billingAddressLastName.required}
              error={translateError(t, fields.billingAddressLastName.error, {
                minLength: 2,
                maxLength: 30,
              })}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="family-name"
                minLength={2}
                maxLength={30}
                {...fieldToInputProps(fields.billingAddressLastName)}
                placeholder={t('checkout_process.last_name')}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.company')}
              required={fields.billingAddressCompany.required}
              error={translateError(t, fields.billingAddressCompany.error)}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="organization"
                {...fieldToInputProps(fields.billingAddressCompany)}
                placeholder={t('checkout_process.company')}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.crn')}
              required={fields.billingAddressCRN.required}
              error={translateError(t, fields.billingAddressCRN.error, {
                maxLength: 8,
              })}
              noLabelElement
            >
              <Input
                type="text"
                maxLength={8}
                {...fieldToInputProps(fields.billingAddressCRN)}
                placeholder={t('checkout_process.crn')}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.vat')}
              required={fields.billingAddressVAT.required}
              error={translateError(t, fields.billingAddressVAT.error, {
                maxLength: 14,
              })}
              noLabelElement
            >
              <Input
                type="text"
                maxLength={14}
                {...fieldToInputProps(fields.billingAddressVAT)}
                placeholder={t('checkout_process.vat')}
              />
            </InputEnvelope>
            <InputEnvelope
              className={styles.street}
              label={t('checkout_process.street')}
              required={fields.billingAddressStreet.required}
              error={translateError(t, fields.billingAddressStreet.error)}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="address-line1"
                {...fieldToInputProps(fields.billingAddressStreet)}
                placeholder={t('checkout_process.street')}
              />
            </InputEnvelope>
            <InputEnvelope
              className={styles.number}
              label={t('checkout_process.number')}
              required={fields.billingAddressNumber.required}
              error={translateError(t, fields.billingAddressNumber.error)}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="address-line2"
                {...fieldToInputProps(fields.billingAddressNumber)}
                placeholder={t('checkout_process.number')}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.city')}
              required={fields.billingAddressCity.required}
              error={translateError(t, fields.billingAddressCity.error)}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="address-level2"
                {...fieldToInputProps(fields.billingAddressCity)}
                placeholder={t('checkout_process.city')}
              />
            </InputEnvelope>
            <InputEnvelope
              label={t('checkout_process.postcode')}
              required={fields.billingAddressPostcode.required}
              error={translateError(t, fields.billingAddressPostcode.error)}
              noLabelElement
            >
              <Input
                type="text"
                autoComplete="postal-code"
                {...fieldToInputProps(fields.billingAddressPostcode)}
                placeholder={t('checkout_process.postcode')}
              />
            </InputEnvelope>
          </div>
        ) : null}

        <div className={styles.paymentMethod}>
          <h2 className={styles.sectionHeading}>
            {t('checkout_process.payment_method')}
          </h2>
          <div className={styles.options}>
            <PaymentSelect {...fieldToPaymentSelectProps(fields.paymentMethod)}>
              {paymentMethods.map((method) => (
                <PaymentSelect.Option
                  key={method.value}
                  value={method.value}
                  label={method.label}
                  icon={method.icon}
                  disabled={
                    isSome(method.maxOrderValue) &&
                    props.orderTotal > method.maxOrderValue
                  }
                  description={
                    isSome(method.maxOrderValue) &&
                    props.orderTotal > method.maxOrderValue
                      ? t('checkout_process.max_order_value_error', {
                          value: formatCurrency(
                            method.maxOrderValue,
                            'CZK',
                            language,
                          ),
                        })
                      : method.description
                  }
                ></PaymentSelect.Option>
              ))}
            </PaymentSelect>
            {fields.paymentMethod.error ? (
              <Error>{translateError(t, fields.paymentMethod.error)}</Error>
            ) : null}
          </div>
        </div>

        <Button type="submit" className={cn(styles.button, styles.submit)}>
          {t('gift_card_configurator.add_to_cart')}
          <Button.Icon placement="right">
            {props.isBusy ? (
              <SpinnerIcon className={cn(styles.arrowIcon)} />
            ) : (
              <ArrowIcon className={cn(styles.arrowIcon)} />
            )}
          </Button.Icon>
        </Button>
        {props.checkoutForm.error ? (
          <Error>{translateError(t, props.checkoutForm.error)}</Error>
        ) : null}
        {props.error != null ? <Error>{props.error}</Error> : null}
      </form>
    </div>
  );
};

interface CardTableProps {
  readonly price: number;
  readonly isGift: boolean;
  readonly fromWho: string;
  readonly toWho: string;
  readonly message: string;
  readonly email: string;
}

const CardTable = (props: CardTableProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  return (
    <table className={styles.cardTable}>
      <tbody>
        <tr className={styles.desktopCombined}>
          <td className={styles.large}>
            <div>
              {t('gift_card_configurator.amount')}
              <span>{formatCurrency(props.price, 'CZK', locale)}</span>
            </div>
          </td>
          <td className={styles.large}>
            <div>
              {t('gift_card_configurator.for_whom')}
              <span>
                {t(
                  `gift_card_configurator.${
                    props.isGift ? 'is_gift' : 'not_gift'
                  }`,
                )}
              </span>
            </div>
          </td>
        </tr>
        <tr className={styles.mobileRow}>
          <td className={styles.large}>{t('gift_card_configurator.amount')}</td>
          <td>{formatCurrency(props.price, 'CZK', locale)}</td>
        </tr>
        <tr className={styles.mobileRow}>
          <td className={styles.large}>
            {t('gift_card_configurator.for_whom')}
          </td>
          <td>
            {t(
              `gift_card_configurator.${props.isGift ? 'is_gift' : 'not_gift'}`,
            )}
          </td>
        </tr>
        <tr>
          <td>{t('gift_card_configurator.from_whom')}</td>
          <td>{props.fromWho}</td>
        </tr>
        <tr>
          <td>{t('gift_card_configurator.for_whom')}</td>
          <td>{props.toWho}</td>
        </tr>
        <tr>
          <td>{t('gift_card_configurator.email')}</td>
          <td>{props.email}</td>
        </tr>
        <tr>
          <td>{t('gift_card_configurator.message_short')}</td>
          <td>{props.message}</td>
        </tr>
      </tbody>
    </table>
  );
};

interface PriceSelectionItemProps {
  readonly price: number;
  readonly active: boolean;
  readonly onClick: () => void;
}

const PriceSelectionItem = (props: PriceSelectionItemProps) => {
  const { locale } = useRouter();
  return (
    <div className={styles.tab}>
      <Button
        size="medium"
        symmetrical={false}
        className={cn({ [styles.selected]: props.active })}
        variant={props.active ? 'secondary' : 'basic'}
        onClick={props.onClick}
      >
        {formatCurrency(props.price, 'CZK', locale)}
      </Button>
    </div>
  );
};

interface PriceSelectionProps {
  readonly products: Product[];
  readonly onClick: (value: string) => void;
}

const PriceSelection = (props: PriceSelectionProps) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<string>(
    props.products[0].itemCode,
  );

  const sortedProducts = [...props.products].sort((a, b) => {
    const priceA = a.unitPrice ?? Infinity;
    const priceB = b.unitPrice ?? Infinity;
    return priceA - priceB;
  });

  const handleClick = (itemCode: string) => {
    setActiveItem(itemCode);
    props.onClick(itemCode);
  };

  return (
    <div className={styles.priceSelection}>
      <div className={styles.title}>{t('gift_card_configurator.amount')}</div>
      <div className={styles.selectors}>
        {sortedProducts.map(
          (product, index) =>
            product.unitPrice && (
              <PriceSelectionItem
                key={index}
                price={product.unitPrice}
                active={product.itemCode === activeItem}
                onClick={() => handleClick(product.itemCode)}
              />
            ),
        )}
      </div>
    </div>
  );
};

const GiftToggle = ({
  isGift,
  setIsGift,
  onNotGiftSelected,
}: {
  isGift: boolean;
  setIsGift: (value: boolean) => void;
  onNotGiftSelected: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <div className={styles.title}>{t('gift_card_configurator.for_whom')}</div>
      <div className={styles.switch}>
        <Button
          size="medium"
          symmetrical={false}
          className={cn(styles.tab, { [styles.selected]: isGift })}
          variant={isGift ? 'secondary' : 'basic'}
          onClick={() => setIsGift(true)}
        >
          {t('gift_card_configurator.is_gift')}
        </Button>
        <Button
          size="medium"
          symmetrical={false}
          className={cn(styles.tab, { [styles.selected]: !isGift })}
          variant={!isGift ? 'secondary' : 'basic'}
          onClick={onNotGiftSelected}
        >
          {t('gift_card_configurator.not_gift')}
        </Button>
      </div>
    </>
  );
};

interface PropsWithChildrenProps {
  readonly activeStep: ActiveStep;
  readonly nextStepName: ActiveStep;
  readonly prevStepName: ActiveStep;
  readonly setActiveStep: (step: ActiveStep) => void;
  readonly form: ReturnType<typeof v2_useInstantOrderCheckout>['form'];
  readonly paymentMethods: ReturnType<
    typeof v2_useInstantOrderCheckout
  >['paymentMethods'];
  readonly error: Maybe<string>;
  readonly isBusy: boolean;
  readonly price: number;
  readonly toWho: string;
  readonly onToWhoChange: (value: string) => void;
  readonly fromWho: string;
  readonly onFromWhoChange: (value: string) => void;
  readonly isGift: boolean;
  readonly onIsGiftChange: (value: boolean) => void;
  readonly message: string;
  readonly onMessageChange: (value: string) => void;
  readonly email: string;
  readonly onEmailChange: (value: string) => void;
  readonly imgUrl?: string | null;
  readonly products: Product[];
  readonly clearConfiguration: () => void;
  readonly onVariantChange: (value: string) => void;
}

const GiftCardDetail: React.FC<
  React.PropsWithChildren<PropsWithChildrenProps>
> = ({ activeStep, prevStepName, nextStepName, setActiveStep, ...props }) => {
  const { t } = useTranslation();

  const onNotGiftSelected = () => {
    props.onToWhoChange('');
    props.onFromWhoChange('');
    props.onMessageChange('');
    props.onIsGiftChange(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.imageContainer}>
        <Image
          className={styles.image}
          src={props.imgUrl}
          alt=""
          layout="fill"
          objectFit="cover"
          priority={false}
          loading={'lazy'}
        />
      </div>
      {activeStep === 'personalize' && props.products ? (
        <PriceSelection
          products={props.products}
          onClick={props.onVariantChange}
        />
      ) : (
        <CardTable
          price={props.price}
          isGift={props.isGift}
          fromWho={props.fromWho}
          toWho={props.toWho}
          message={props.message}
          email={props.email}
        />
      )}
      <div className={styles.formSection}>
        {activeStep === 'personalize' && (
          <GiftToggle
            isGift={props.isGift}
            setIsGift={props.onIsGiftChange}
            onNotGiftSelected={onNotGiftSelected}
          />
        )}
        {activeStep === 'personalize' ? (
          <CardForm
            isGift={props.isGift}
            toWho={props.toWho}
            onToWhoChange={props.onToWhoChange}
            fromWho={props.fromWho}
            onFromWhoChange={props.onFromWhoChange}
            message={props.message}
            onMessageChange={props.onMessageChange}
            email={props.email}
            onEmailChange={props.onEmailChange}
            onSubmit={() => setActiveStep(nextStepName)}
          />
        ) : (
          <CardCheckoutForm
            checkoutForm={props.form}
            paymentMethods={props.paymentMethods}
            orderTotal={props.price}
            isBusy={props.isBusy}
            error={props.error}
          />
        )}
      </div>
      <div className={styles.controls}>
        <Button
          className={styles.button}
          variant="basic"
          onClick={() =>
            activeStep === 'check'
              ? setActiveStep(prevStepName)
              : props.clearConfiguration()
          }
        >
          {t(`gift_card_configurator.back_to_${prevStepName}`)}
          <Button.Icon placement="left">
            <ArrowIcon className={cn(styles.arrowIcon, styles.left)} />
          </Button.Icon>
        </Button>
      </div>
    </div>
  );
};

export default GiftCardDetail;
